import PropTypes from 'prop-types';
import { useEffect } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
import { useAuthContext } from 'src/auth/hooks';
// components
import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { usePathname } from 'src/routes/hooks';
import { NavSectionVertical } from 'src/components/nav-section';
//
import { NAV } from '../config-layout';
import { useNavData } from './config-navigation';
import { NavToggleButton, NavUpgrade } from '../_common';
import bannerWorkshop from '../../assets/illustrations/banner_workshop_60.jpeg';
import { useLocales } from 'src/locales';
import { Card, CardContent, Typography, Button, Link } from '@mui/material';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  const { user } = useAuthContext();
  const { translate } = useLocales();

  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 1 }} />

      <NavSectionVertical
        data={navData}
        config={{
          currentRole: user?.role || 'admin',
        }}
      />

      <Stack spacing={2} sx={{ px: 2, mt: 3 }}>
        {/* Practicum Banner */}
        <Card sx={{
          height: 120,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          backgroundImage: `url(${bannerWorkshop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          borderRadius: 2,
          boxShadow: 3,
        }}>
          <Box sx={{ p: 1.5, zIndex: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              href="https://t.me/notibotapp/128"
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
            >
              {translate('banners.practicum.button')}
            </Button>
          </Box>
        </Card>

        {/* News Banner */}
        <Card sx={{
          height: 120,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          background: 'linear-gradient(225deg, #FF3CAC 0%, #784BA0 50%, #2B86C5 100%)',
          color: 'common.white',
          borderRadius: 2,
          boxShadow: 3,
        }}>
          <CardContent sx={{ p: 1.5, pb: 0 }}>
            <Typography variant="subtitle2" component="div" sx={{ color: 'common.white', fontWeight: 'bold', lineHeight: 1.2 }}>
              Что происходит в Нотибот сейчас?
            </Typography>
          </CardContent>
          <Box sx={{ p: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: 'common.white',
                color: 'common.black',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
              }}
              href="https://t.me/notibotapp"
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
            >
              Перейти
            </Button>
          </Box>
        </Card>
      </Stack>

      <Box sx={{ flexGrow: 1 }} />

      <NavUpgrade />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};
